import { useState, useEffect, useRef, useMemo } from "react";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { motion } from "framer-motion";
import axios from "axios";
import Layout from "../components/Layout";
import { ReceiptPdfFrame } from "../components/ReceiptPdfFrame";
import EditableText from "../components/EditableText";
import TaxonomyManager from "../components/TaxonomyManager";
import { useTaxonomy } from "../hooks/useTaxonomy";
import {
  Plus, Upload, DollarSign, TrendingUp, FileText, Download,
  Edit2, Trash2, Receipt, AlertTriangle, ChevronLeft,
  ChevronRight, Search, Filter, Paperclip, Eye, Package, Loader2, X
} from "lucide-react";

// Fallback labels used only if the live taxonomy fetch returns nothing
// (e.g. cold-cache + 500 on /api/taxonomy/expense_categories). The
// canonical source is the founder-managed taxonomy DB.
const FALLBACK_CATEGORY_LABELS = {
  loupes_equipment: "Loupes / Clinical Equipment",
  instruments: "Instruments / Hygiene Tools",
  scrubs_uniforms: "Scrubs / Uniforms",
  continuing_education: "Continuing Education (CE)",
  licensing_fees: "Licensing / Registration Fees",
  mileage_travel: "Mileage / Travel",
  insurance: "Insurance (Professional/Disability)",
  software_subscriptions: "Software / Subscriptions",
  miscellaneous: "Miscellaneous Business Expenses",
};

const CATEGORY_COLORS = {
  loupes_equipment: "#6366F1",
  instruments: "#6B4E8A",
  scrubs_uniforms: "#F0ECF7",
  continuing_education: "#8DB5A0",
  licensing_fees: "#8DB5A0",
  mileage_travel: "#10B981",
  insurance: "#3B82F6",
  software_subscriptions: "#8B5CF6",
  miscellaneous: "#64748B",
};

const FinancePage = () => {
  const { user } = useAuth();
  const { items: categoryItems, refetch: refetchCategories } = useTaxonomy("expense_categories");
  const [dashboard, setDashboard] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [filterCategory, setFilterCategory] = useState("");
  const [receiptFilter, setReceiptFilter] = useState("all"); // all | yes | no
  const [searchQuery, setSearchQuery] = useState("");
  const [viewingReceipt, setViewingReceipt] = useState(null); // {expense_id, receipt_content_type}
  const [pendingReceiptFile, setPendingReceiptFile] = useState(null); // for Add Expense
  const [attachingReceiptToExpense, setAttachingReceiptToExpense] = useState(false);
  const [exportingAudit, setExportingAudit] = useState(false);
  const fileInputRef = useRef(null);
  const addExpenseReceiptRef = useRef(null);
  const editExpenseReceiptRef = useRef(null);

  // Dynamic label map driven by the founder-managed taxonomy. Falls back
  // to the static seed labels while the API call is in flight or returns
  // empty, so the page never renders bare keys.
  const categoryLabels = useMemo(() => {
    const map = { ...FALLBACK_CATEGORY_LABELS };
    (categoryItems || []).forEach((it) => {
      if (it.key) map[it.key] = it.label || map[it.key] || it.key;
    });
    return map;
  }, [categoryItems]);

  // Ordered list of {key,label} used in <Select> dropdowns. Empty taxonomy
  // → fall back to the static list so users always have categories to pick.
  const categoryOptions = useMemo(() => {
    if (categoryItems && categoryItems.length > 0) {
      return categoryItems.map((it) => ({ key: it.key, label: it.label }));
    }
    return Object.entries(FALLBACK_CATEGORY_LABELS).map(([key, label]) => ({ key, label }));
  }, [categoryItems]);

  const [expenseForm, setExpenseForm] = useState({
    date: new Date().toISOString().split("T")[0],
    amount: "",
    category: "miscellaneous",
    description: "",
  });

  useEffect(() => {
    fetchDashboard();
  }, [selectedYear, receiptFilter]);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ year: selectedYear });
      if (receiptFilter !== "all") params.set("has_receipt", receiptFilter);
      const [dashRes, expRes] = await Promise.all([
        axios.get(`${API}/finance/dashboard?year=${selectedYear}`),
        axios.get(`${API}/finance/expenses?${params.toString()}`)
      ]);
      setDashboard(dashRes.data);
      setExpenses(expRes.data.expenses);
    } catch (error) {
      toast.error("Failed to load financial data");
    } finally {
      setLoading(false);
    }
  };

  const handleAddExpense = async (e) => {
    e.preventDefault();
    if (!expenseForm.amount || !expenseForm.date) {
      toast.error("Please fill in date and amount");
      return;
    }
    setSubmitting(true);
    try {
      const created = await axios.post(`${API}/finance/expenses`, {
        ...expenseForm,
        amount: parseFloat(expenseForm.amount)
      });
      // If the member attached a receipt while filling the form, upload it
      // now against the new expense_id so the link is permanent.
      if (pendingReceiptFile) {
        try {
          const fd = new FormData();
          fd.append("file", pendingReceiptFile);
          await axios.post(
            `${API}/finance/expenses/${created.data.expense_id}/receipt`,
            fd,
            { headers: { "Content-Type": "multipart/form-data" } }
          );
        } catch (err) {
          toast.warning("Expense saved, but receipt upload failed. You can attach it later via Edit.");
        }
      }
      toast.success(pendingReceiptFile ? "Expense saved with receipt." : "Expense added!");
      setShowAddExpense(false);
      setExpenseForm({ date: new Date().toISOString().split("T")[0], amount: "", category: "miscellaneous", description: "" });
      setPendingReceiptFile(null);
      if (addExpenseReceiptRef.current) addExpenseReceiptRef.current.value = "";
      fetchDashboard();
    } catch (error) {
      toast.error("Failed to add expense");
    } finally {
      setSubmitting(false);
    }
  };

  // Attach a receipt file to an EXISTING expense (used from the Edit dialog).
  const attachReceiptToExpense = async (expenseId, file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error("Receipt must be under 15MB.");
      return;
    }
    setAttachingReceiptToExpense(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(
        `${API}/finance/expenses/${expenseId}/receipt`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success("Receipt attached.");
      // Update local state so the Edit dialog reflects it immediately
      setEditingExpense((cur) => cur && cur.expense_id === expenseId
        ? { ...cur, receipt_filename: res.data.receipt_filename, receipt_uploaded_at: res.data.receipt_uploaded_at, receipt_path: "attached" }
        : cur);
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to attach receipt.");
    } finally {
      setAttachingReceiptToExpense(false);
      if (editExpenseReceiptRef.current) editExpenseReceiptRef.current.value = "";
    }
  };

  const detachReceipt = async (expenseId) => {
    if (!window.confirm("Remove this receipt from the expense?")) return;
    try {
      await axios.delete(`${API}/finance/expenses/${expenseId}/receipt`);
      toast.success("Receipt removed from expense.");
      setEditingExpense((cur) => cur && cur.expense_id === expenseId
        ? { ...cur, receipt_path: null, receipt_filename: null, receipt_uploaded_at: null }
        : cur);
      fetchDashboard();
    } catch (err) {
      toast.error("Could not remove receipt.");
    }
  };

  const downloadAuditPackage = async () => {
    setExportingAudit(true);
    try {
      const res = await axios.get(`${API}/finance/export/audit-package?year=${selectedYear}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `DHDH_Audit_Package_${selectedYear}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Could not build audit package.");
    } finally {
      setExportingAudit(false);
    }
  };

  const handleUploadReceipt = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error("File must be under 15MB");
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await axios.post(`${API}/finance/expenses/upload-receipt`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Receipt uploaded! Review the extracted data.");
      setShowUpload(false);
      setEditingExpense(response.data);
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload receipt");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleUpdateExpense = async (e) => {
    e.preventDefault();
    if (!editingExpense) return;
    setSubmitting(true);
    try {
      await axios.put(`${API}/finance/expenses/${editingExpense.expense_id}`, {
        date: editingExpense.date,
        amount: parseFloat(editingExpense.amount),
        category: editingExpense.category,
        description: editingExpense.description
      });
      toast.success("Expense updated!");
      setEditingExpense(null);
      fetchDashboard();
    } catch (error) {
      toast.error("Failed to update");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    if (!window.confirm("Delete this expense?")) return;
    try {
      await axios.delete(`${API}/finance/expenses/${expenseId}`);
      toast.success("Expense deleted");
      fetchDashboard();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const handleExportCSV = () => {
    const url = `${API}/finance/export/csv?year=${selectedYear}`;
    window.open(url, "_blank");
  };

  const formatCurrency = (amount) => new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(amount || 0);

  const filteredExpenses = expenses.filter(e => {
    if (filterCategory && (e.confirmed_category || e.category) !== filterCategory) return false;
    if (searchQuery && !e.description?.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const topCategories = dashboard?.category_breakdown
    ? Object.entries(dashboard.category_breakdown).sort((a, b) => b[1] - a[1])
    : [];

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12" data-testid="finance-page">
        {/* Disclaimer */}
        <div className="bg-[#F0ECF7]/70 border border-[#E5DFEC] rounded-xl p-3 mb-6 flex items-start gap-2" data-testid="finance-disclaimer">
          <AlertTriangle className="w-4 h-4 text-[#6B4E8A] mt-0.5 shrink-0" />
          <p className="text-xs text-[#6B4E8A] leading-relaxed">
            All financial and tax information is provided for organizational purposes only. DHDH is not a licensed tax advisor. Always verify your information with a qualified CPA or the CRA directly.
          </p>
        </div>

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-medium text-[#6B4E8A] inline-flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <EditableText blockKey="finance.title" fallback="Financial Organizer" as="span" />
              <TaxonomyManager
                kind="expense_categories"
                title="Expense categories"
                description="Manage the categories members can assign to receipts."
                triggerLabel="Manage expense categories"
                onChange={refetchCategories}
              />
            </h1>
            <EditableText
              blockKey="finance.subtitle"
              fallback="Track receipts, categorize expenses, export summaries"
              as="p"
              className="text-[#64748B] mt-1"
              multiline
            />
          </div>
          <div className="flex gap-3 flex-wrap">
            <Dialog open={showUpload} onOpenChange={setShowUpload}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-full border-[#8DB5A0] text-[#8DB5A0] hover:bg-[#8DB5A0]/10" data-testid="upload-receipt-btn">
                  <Upload className="w-4 h-4 mr-2" /> Upload Receipt
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Upload Receipt</DialogTitle>
                </DialogHeader>
                <div className="mt-4">
                  <div className="bg-[#FAF7F2] rounded-xl p-6 text-center border-2 border-dashed border-[#E5DFEC]">
                    <Receipt className="w-12 h-12 mx-auto mb-4 text-[#8DB5A0]" />
                    <p className="text-[#6B4E8A] font-medium mb-2">Upload your receipt</p>
                    <p className="text-sm text-[#64748B] mb-4">AI will extract the amount, date, and suggest a category</p>
                    <input ref={fileInputRef} type="file" accept="image/jpeg,image/jpg,image/png,image/heic,image/heif,application/pdf" onChange={handleUploadReceipt} className="hidden" id="receipt-upload" data-testid="receipt-file-input" />
                    <Button type="button" onClick={() => fileInputRef.current?.click()} className="bg-[#8DB5A0] text-white hover:bg-[#D0694E] rounded-full" disabled={uploading}>
                      {uploading ? "Analyzing..." : "Select File"}
                    </Button>
                  </div>
                  <p className="text-xs text-[#64748B] text-center mt-3">JPG, PNG, PDF, HEIC (max 15MB)</p>
                </div>
              </DialogContent>
            </Dialog>
            <Dialog open={showAddExpense} onOpenChange={setShowAddExpense}>
              <DialogTrigger asChild>
                <Button className="bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full" data-testid="add-expense-btn">
                  <Plus className="w-4 h-4 mr-2" /> Add Expense
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Add Expense</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddExpense} className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Date *</Label>
                      <Input type="date" value={expenseForm.date} onChange={(e) => setExpenseForm({ ...expenseForm, date: e.target.value })} className="mt-2 bg-white border-[#E5DFEC]" required data-testid="expense-date-input" />
                    </div>
                    <div>
                      <Label>Amount (CAD) *</Label>
                      <Input type="number" step="0.01" min="0" value={expenseForm.amount} onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })} className="mt-2 bg-white border-[#E5DFEC]" placeholder="0.00" required data-testid="expense-amount-input" />
                    </div>
                  </div>
                  <div>
                    <Label>Category</Label>
                    <Select value={expenseForm.category} onValueChange={(v) => setExpenseForm({ ...expenseForm, category: v })}>
                      <SelectTrigger className="mt-2 bg-white border-[#E5DFEC]" data-testid="expense-category-trigger"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {categoryOptions.map((opt) => (
                          <SelectItem key={opt.key} value={opt.key} data-testid={`expense-category-${opt.key}`}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea value={expenseForm.description} onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })} placeholder="What was this expense for?" className="mt-2 bg-white border-[#E5DFEC]" data-testid="expense-description-input" />
                  </div>
                  <div>
                    <Label className="flex items-center gap-1.5">
                      <Paperclip size={12} /> Receipt <span className="text-[#94A3B8] font-normal">(optional — you can attach later)</span>
                    </Label>
                    <input
                      ref={addExpenseReceiptRef}
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/heic,image/heif,application/pdf"
                      onChange={(e) => setPendingReceiptFile(e.target.files?.[0] || null)}
                      className="mt-2 block w-full text-sm text-[#64748B] file:mr-3 file:py-1.5 file:px-3 file:rounded-full file:border-0 file:bg-[#F0ECF7] file:text-[#6B4E8A] file:font-medium hover:file:bg-[#E5DFEC]"
                      data-testid="add-expense-receipt-input"
                    />
                    {pendingReceiptFile && (
                      <p className="text-xs text-[#3F7C5C] mt-1 flex items-center gap-1">
                        <Paperclip size={10} /> {pendingReceiptFile.name} ({(pendingReceiptFile.size / 1024).toFixed(0)} KB)
                      </p>
                    )}
                    <p className="text-xs text-[#94A3B8] mt-1">JPG, PNG, PDF, HEIC · max 15MB</p>
                  </div>
                  <div className="flex justify-end gap-3 pt-2">
                    <Button type="button" variant="outline" onClick={() => { setShowAddExpense(false); setPendingReceiptFile(null); }} className="rounded-full">Cancel</Button>
                    <Button type="submit" className="bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full" disabled={submitting} data-testid="submit-expense-btn">
                      {submitting ? "Saving..." : "Add Expense"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {loading ? (
          <div className="space-y-6">{[1,2,3].map(i => <div key={i} className="h-32 bg-[#F0ECF7] rounded-2xl animate-pulse" />)}</div>
        ) : (
          <>
            {/* Year Selector & Export */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <button onClick={() => setSelectedYear(y => y - 1)} className="p-2 hover:bg-[#F0ECF7] rounded-lg"><ChevronLeft className="w-5 h-5" /></button>
                <h2 className="text-xl font-medium text-[#6B4E8A]" style={{ fontFamily: 'Outfit, sans-serif' }}>{selectedYear}</h2>
                <button onClick={() => setSelectedYear(y => y + 1)} className="p-2 hover:bg-[#F0ECF7] rounded-lg"><ChevronRight className="w-5 h-5" /></button>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={handleExportCSV} className="rounded-full" data-testid="export-csv-btn">
                  <Download className="w-4 h-4 mr-2" /> CSV
                </Button>
                <Button variant="outline" onClick={() => window.open(`${API}/finance/export/pdf?year=${selectedYear}`, "_blank")} className="rounded-full" data-testid="export-pdf-btn">
                  <FileText className="w-4 h-4 mr-2" /> PDF
                </Button>
                <Button
                  variant="outline"
                  onClick={downloadAuditPackage}
                  disabled={exportingAudit}
                  className="rounded-full border-[#8DB5A0] text-[#3F7C5C] hover:bg-[#8DB5A0]/10"
                  data-testid="audit-package-btn"
                  title="Download PDF summary + every attached receipt as a ZIP for CRA audit submission"
                >
                  {exportingAudit
                    ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    : <Package className="w-4 h-4 mr-2" />}
                  Audit package (ZIP)
                </Button>
              </div>
            </div>

            {/* Dashboard Cards */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
              <div className="bg-gradient-to-br from-[#6B4E8A] to-[#553D6F] text-white rounded-2xl p-6">
                <DollarSign className="w-6 h-6 mb-2 text-[#8DB5A0]" />
                <p className="text-3xl font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>{formatCurrency(dashboard?.yearly_total)}</p>
                <p className="text-sm text-white/60 mt-1">{selectedYear} Total</p>
              </div>
              <div className="bg-white border border-[#E5DFEC] rounded-2xl p-6">
                <FileText className="w-6 h-6 mb-2 text-[#8DB5A0]" />
                <p className="text-3xl font-medium text-[#6B4E8A]" style={{ fontFamily: 'Outfit, sans-serif' }}>{dashboard?.expense_count || 0}</p>
                <p className="text-sm text-[#64748B] mt-1">Expenses</p>
              </div>
            </motion.div>

            {/* Category Breakdown */}
            {topCategories.length > 0 && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white border border-[#E5DFEC] rounded-2xl p-6 mb-8">
                <h3 className="font-medium text-[#6B4E8A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Category Breakdown</h3>
                <div className="space-y-3">
                  {topCategories.map(([cat, amount]) => {
                    const pct = dashboard?.yearly_total ? (amount / dashboard.yearly_total * 100) : 0;
                    return (
                      <div key={cat}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-[#6B4E8A] truncate flex-1">{categoryLabels[cat] || cat}</span>
                          <span className="text-[#64748B] ml-2">{formatCurrency(amount)} ({pct.toFixed(0)}%)</span>
                        </div>
                        <div className="w-full bg-[#F0ECF7] rounded-full h-2">
                          <div className="h-2 rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: CATEGORY_COLORS[cat] || "#64748B" }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {/* Expenses List */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white border border-[#E5DFEC] rounded-2xl p-6">
              {/* Receipt-section disclaimer (Phase 67) */}
              <div className="bg-[#FAF7F2] border border-[#E5DFEC] rounded-lg p-3 mb-4 flex items-start gap-2" data-testid="receipt-disclaimer">
                <Paperclip className="w-4 h-4 text-[#6B4E8A] mt-0.5 shrink-0" />
                <p className="text-xs text-[#64748B] leading-relaxed">
                  DHDH stores your receipt images for your personal records. Always verify your expense documentation meets current CRA requirements. DHDH is not responsible for audit outcomes.
                </p>
              </div>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                <h3 className="font-medium text-[#6B4E8A]" style={{ fontFamily: 'Outfit, sans-serif' }}>Expenses</h3>
                <div className="flex gap-2 flex-wrap">
                  <div className="relative flex-1 md:w-48">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
                    <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search..." className="pl-9 bg-[#FAF7F2] border-[#E5DFEC] h-9 text-sm" />
                  </div>
                  <Select value={filterCategory || "all"} onValueChange={(v) => setFilterCategory(v === "all" ? "" : v)}>
                    <SelectTrigger className="w-44 bg-[#FAF7F2] border-[#E5DFEC] h-9 text-sm" data-testid="filter-category-trigger">
                      <SelectValue placeholder="All Categories" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {categoryOptions.map((opt) => (
                        <SelectItem key={opt.key} value={opt.key} data-testid={`filter-category-${opt.key}`}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={receiptFilter} onValueChange={setReceiptFilter}>
                    <SelectTrigger className="w-44 bg-[#FAF7F2] border-[#E5DFEC] h-9 text-sm" data-testid="filter-receipt-trigger">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all" data-testid="filter-receipt-all">All receipts</SelectItem>
                      <SelectItem value="yes" data-testid="filter-receipt-yes">Receipt attached</SelectItem>
                      <SelectItem value="no" data-testid="filter-receipt-no">Missing receipt</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {filteredExpenses.length > 0 ? (
                <div className="space-y-2">
                  {filteredExpenses.map((exp) => {
                    const cat = exp.confirmed_category || exp.category;
                    const hasReceipt = !!exp.receipt_path || !!exp.receipt_url;
                    return (
                      <div key={exp.expense_id} className="flex items-center justify-between p-3 bg-[#FAF7F2] rounded-xl group" data-testid={`expense-row-${exp.expense_id}`}>
                        <button
                          type="button"
                          onClick={() => hasReceipt
                            ? setViewingReceipt(exp)
                            : setEditingExpense(exp)
                          }
                          className="flex items-center gap-3 flex-1 min-w-0 text-left"
                          title={hasReceipt ? "Tap to view receipt" : "Tap to edit / add receipt"}
                        >
                          <div className="w-2 h-10 rounded-full shrink-0" style={{ backgroundColor: CATEGORY_COLORS[cat] || "#64748B" }} />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-[#6B4E8A] truncate">{exp.description || categoryLabels[cat] || cat}</p>
                            <p className="text-xs text-[#64748B] flex items-center gap-1.5">
                              {exp.date}
                              {exp.ai_suggestion && !exp.confirmed_category && <span className="text-[#8DB5A0]">· AI suggested</span>}
                            </p>
                          </div>
                        </button>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className="font-medium text-[#6B4E8A]">{formatCurrency(exp.amount)}</span>
                          {hasReceipt ? (
                            <button
                              type="button"
                              onClick={() => setViewingReceipt(exp)}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#8DB5A0]/15 text-[#3F7C5C] text-[10px] font-medium uppercase tracking-wider hover:bg-[#8DB5A0]/25"
                              data-testid={`receipt-badge-${exp.expense_id}`}
                              title="View attached receipt"
                            >
                              <Paperclip size={10} /> Receipt
                            </button>
                          ) : (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FEE2E2] text-[#B91C1C] text-[10px] font-medium uppercase tracking-wider"
                              data-testid={`receipt-missing-${exp.expense_id}`}
                              title="No receipt attached — add one to be audit-ready"
                            >
                              <AlertTriangle size={10} /> Missing
                            </span>
                          )}
                          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => setEditingExpense(exp)} className="p-1.5 hover:bg-[#E5DFEC] rounded-lg" data-testid={`edit-expense-${exp.expense_id}`}><Edit2 className="w-3.5 h-3.5 text-[#64748B]" /></button>
                            <button onClick={() => handleDeleteExpense(exp.expense_id)} className="p-1.5 hover:bg-[#EF4444]/10 rounded-lg"><Trash2 className="w-3.5 h-3.5 text-[#EF4444]" /></button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Receipt className="w-12 h-12 mx-auto mb-4 text-[#E5DFEC]" />
                  <h3 className="text-lg font-medium text-[#6B4E8A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>No expenses yet</h3>
                  <p className="text-[#64748B] mb-4">Start by adding an expense or uploading a receipt</p>
                </div>
              )}
            </motion.div>
          </>
        )}

        {/* Edit Expense Dialog */}
        <Dialog open={!!editingExpense} onOpenChange={() => setEditingExpense(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                {editingExpense?.ai_suggestion && !editingExpense?.confirmed_category ? "Review AI Extraction" : "Edit Expense"}
              </DialogTitle>
            </DialogHeader>
            {editingExpense && (
              <form onSubmit={handleUpdateExpense} className="space-y-4 mt-4">
                {editingExpense.ai_suggestion && !editingExpense.confirmed_category && (
                  <div className="bg-[#F0ECF7] border border-[#E5DFEC] p-3 rounded-lg space-y-1.5" data-testid="ai-suggestion-banner">
                    <p className="text-sm text-[#6B4E8A] font-medium">AI suggested CRA category</p>
                    <p className="text-xs text-[#64748B]">
                      Auto-categorized as <strong className="text-[#6B4E8A]">{categoryLabels[editingExpense.ai_suggestion] || editingExpense.ai_suggestion}</strong> based on receipt content. Confirm below or pick a different category — your final choice is what&apos;s reported.
                    </p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Date</Label>
                    <Input type="date" value={editingExpense.date} onChange={(e) => setEditingExpense({ ...editingExpense, date: e.target.value })} className="mt-2 bg-white border-[#E5DFEC]" />
                  </div>
                  <div>
                    <Label>Amount (CAD)</Label>
                    <Input type="number" step="0.01" value={editingExpense.amount} onChange={(e) => setEditingExpense({ ...editingExpense, amount: e.target.value })} className="mt-2 bg-white border-[#E5DFEC]" />
                  </div>
                </div>
                <div>
                  <Label>Category</Label>
                  <Select value={editingExpense.category} onValueChange={(v) => setEditingExpense({ ...editingExpense, category: v })}>
                    <SelectTrigger className="mt-2 bg-white border-[#E5DFEC]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {categoryOptions.map((opt) => (
                        <SelectItem key={opt.key} value={opt.key}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea value={editingExpense.description} onChange={(e) => setEditingExpense({ ...editingExpense, description: e.target.value })} className="mt-2 bg-white border-[#E5DFEC]" />
                </div>
                {/* Receipt management within Edit (works at any time, not just at creation) */}
                <div className="border-t border-[#E5DFEC] pt-3">
                  <Label className="flex items-center gap-1.5">
                    <Paperclip size={12} /> Receipt
                  </Label>
                  {(editingExpense.receipt_path || editingExpense.receipt_url) ? (
                    <div className="mt-2 flex items-center justify-between bg-[#FAF7F2] border border-[#E5DFEC] rounded-lg px-3 py-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <Paperclip size={14} className="text-[#3F7C5C] shrink-0" />
                        <span className="text-sm text-[#6B4E8A] truncate">
                          {editingExpense.receipt_filename || "Attached receipt"}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button type="button" size="sm" variant="ghost" onClick={() => setViewingReceipt(editingExpense)} className="text-[#6B4E8A]" data-testid="view-receipt-from-edit-btn">
                          <Eye size={14} className="mr-1" /> View
                        </Button>
                        <Button type="button" size="sm" variant="ghost" onClick={() => detachReceipt(editingExpense.expense_id)} className="text-[#DC2626]" data-testid="detach-receipt-btn">
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-2 bg-[#FAF7F2] border border-dashed border-[#E5DFEC] rounded-lg p-3 text-center">
                      <p className="text-xs text-[#94A3B8] mb-2">No receipt attached yet</p>
                      <input
                        ref={editExpenseReceiptRef}
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/heic,image/heif,application/pdf"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) attachReceiptToExpense(editingExpense.expense_id, f);
                        }}
                        className="hidden"
                        id="edit-receipt-upload"
                        data-testid="edit-receipt-file-input"
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => editExpenseReceiptRef.current?.click()}
                        disabled={attachingReceiptToExpense}
                        className="rounded-full"
                        data-testid="attach-receipt-btn"
                      >
                        {attachingReceiptToExpense ? <Loader2 size={14} className="animate-spin mr-1" /> : <Upload size={14} className="mr-1" />}
                        Attach receipt
                      </Button>
                      <p className="text-[10px] text-[#94A3B8] mt-2">JPG, PNG, PDF, HEIC · max 15MB</p>
                    </div>
                  )}
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setEditingExpense(null)} className="rounded-full">Cancel</Button>
                  <Button type="submit" className="bg-[#10B981] text-white hover:bg-[#059669] rounded-full" disabled={submitting} data-testid="confirm-expense-btn">
                    {submitting ? "Saving..." : "Confirm"}
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* View Receipt Modal — full-size preview */}
        <Dialog open={!!viewingReceipt} onOpenChange={(o) => !o && setViewingReceipt(null)}>
          <DialogContent className="max-w-4xl max-h-[95vh] p-0 overflow-hidden" data-testid="view-receipt-dialog">
            <DialogHeader className="px-5 py-3 border-b border-[#E5DFEC] flex flex-row items-center justify-between">
              <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }} className="flex items-center gap-2">
                <Paperclip size={16} /> Receipt {viewingReceipt?.receipt_filename ? `· ${viewingReceipt.receipt_filename}` : ""}
              </DialogTitle>
              {viewingReceipt && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(`${API}/finance/expenses/${viewingReceipt.expense_id}/receipt`, "_blank")}
                  className="rounded-full"
                  data-testid="open-receipt-newtab-btn"
                >
                  <Download size={14} className="mr-1" /> Open in new tab
                </Button>
              )}
            </DialogHeader>
            {viewingReceipt && (
              <div className="overflow-auto bg-[#0F172A]/5 max-h-[80vh]">
                {(viewingReceipt.receipt_content_type || "").includes("pdf") || (viewingReceipt.receipt_filename || "").toLowerCase().endsWith(".pdf") ? (
                  <ReceiptPdfFrame url={`${API}/finance/expenses/${viewingReceipt.expense_id}/receipt`} />
                ) : (
                  <img
                    src={`${API}/finance/expenses/${viewingReceipt.expense_id}/receipt`}
                    alt="Receipt"
                    className="block max-w-full mx-auto"
                    data-testid="receipt-image"
                  />
                )}
                <div className="bg-white px-5 py-3 border-t border-[#E5DFEC] text-xs text-[#64748B] flex flex-wrap gap-x-4 gap-y-1">
                  <span><strong className="text-[#6B4E8A]">Date:</strong> {viewingReceipt.date}</span>
                  <span><strong className="text-[#6B4E8A]">Amount:</strong> {formatCurrency(viewingReceipt.amount)}</span>
                  <span><strong className="text-[#6B4E8A]">Category:</strong> {categoryLabels[viewingReceipt.confirmed_category || viewingReceipt.category] || viewingReceipt.category}</span>
                  {viewingReceipt.receipt_uploaded_at && (
                    <span><strong className="text-[#6B4E8A]">Uploaded:</strong> {new Date(viewingReceipt.receipt_uploaded_at).toLocaleString("en-CA")}</span>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default FinancePage;
